import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft, Lock, Check, ChevronDown, BookOpen, PenLine, Moon, Palette, RotateCcw } from "lucide-react";
import DayFlow from "@/components/mental/DayFlow";
import MentalHero from "@/components/mental/MentalHero";
import InProgramme from "@/components/mental/InProgramme";
import { TALK, CORES, readOf, READING_LINES, WEEKS, DAYS, OFFERINGS, EXPECT } from "@/lib/mentalPlan";
import useProgress from "@/lib/useProgress";
import { PLATFORMS } from "@/lib/platforms";

const PAPER = "#F7F3FB";
const INK = "#2E2640";
const SOFT = "rgba(46,38,64,0.55)";
const HERO = "https://images.unsplash.com/photo-1628538746227-21f21300bced?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

/* ------------------------------------------------------------ the talk */
function Talk({ onDone, onLeave }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const t = TALK[step];
  const prompt = t.prompt.replace("{name}", name.trim() || "you");

  const advance = (v) => {
    const a = { ...answers, [t.id]: v };
    setAnswers(a);
    if (step + 1 >= TALK.length) onDone(a, name.trim());
    else setStep(step + 1);
  };

  return (
    <div className="w-full max-w-2xl" data-testid="mental-talk">
      <div className="flex gap-1.5 mb-8 max-w-[220px]">
        {TALK.map((_, i) => (
          <span key={i} className="h-[3px] flex-1 rounded-full overflow-hidden" style={{ background: "rgba(46,38,64,0.12)" }}>
            <motion.span className="block h-full" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.4 }} style={{ background: "#6F5AA8" }} />
          </span>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={t.id} initial={{ opacity: 0, y: 24, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={{ opacity: 0, y: -18, filter: "blur(6px)" }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.06]" style={{ color: INK }}>{prompt}</h2>
          <p className="text-[15px] mt-3.5 max-w-lg" style={{ color: SOFT }}>{t.sub}</p>

          {t.kind === "text" ? (
            <form onSubmit={(e) => { e.preventDefault(); advance(0); }} className="mt-8 flex flex-wrap items-center gap-3">
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t.placeholder}
                className="flex-1 min-w-[200px] bg-transparent border-b outline-none font-display text-2xl sm:text-3xl pb-3 transition-colors"
                style={{ borderColor: "rgba(46,38,64,0.2)", color: INK }} data-testid="talk-name" />
              <button type="submit" disabled={!name.trim()} className="rounded-full px-6 py-3.5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-40" style={{ background: "#6F5AA8" }} data-testid="talk-name-next">
                Go on <ArrowRight size={14} />
              </button>
            </form>
          ) : (
            <div className="mt-8 space-y-2.5">
              {t.options.map((o, i) => (
                <motion.button key={o.label} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.06 }}
                  onClick={() => advance(i)}
                  className="group w-full text-left rounded-2xl px-5 py-4 flex items-center gap-4 transition-all hover:-translate-y-0.5"
                  style={{ background: "rgba(255,255,255,0.78)", border: "1px solid rgba(46,38,64,0.08)", backdropFilter: "blur(6px)" }}
                  data-testid={`talk-option-${step}-${i}`}>
                  <span className="w-2 h-2 rounded-full shrink-0 transition-colors" style={{ background: "rgba(111,90,168,0.3)" }} />
                  <span className="font-display text-[17px] sm:text-lg leading-snug" style={{ color: INK }}>{o.label}</span>
                  <ArrowRight size={14} className="ml-auto shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" style={{ color: "#6F5AA8" }} />
                </motion.button>
              ))}
            </div>
          )}

          <button onClick={() => (step > 0 ? setStep(step - 1) : onLeave())} className="mt-8 text-xs inline-flex items-center gap-1.5" style={{ color: "rgba(46,38,64,0.58)" }} data-testid="talk-back">
            <ArrowLeft size={11} /> {step > 0 ? "back" : "not now"}
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* --------------------------------------------------------- the reading */
function Reading({ onDone }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (n >= READING_LINES.length) { const t = setTimeout(onDone, 520); return () => clearTimeout(t); }
    const t = setTimeout(() => setN(n + 1), 760);
    return () => clearTimeout(t);
  }, [n, onDone]);
  return (
    <div className="w-full max-w-lg" data-testid="mental-reading">
      <div className="relative w-16 h-16">
        <motion.span className="absolute inset-0 rounded-full" style={{ border: "2px solid rgba(111,90,168,0.2)" }} />
        <motion.span className="absolute inset-0 rounded-full" style={{ borderTop: "2px solid #6F5AA8", borderRight: "2px solid transparent", borderBottom: "2px solid transparent", borderLeft: "2px solid transparent" }}
          animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} />
      </div>
      <div className="mt-8 space-y-3">
        {READING_LINES.map((l, i) => (
          <motion.p key={l} initial={{ opacity: 0, x: 12 }} animate={{ opacity: i < n ? 1 : 0.18, x: i < n ? 0 : 12 }} transition={{ duration: 0.45 }}
            className="font-display text-xl sm:text-2xl flex items-center gap-3" style={{ color: INK }}>
            <span className="w-4 shrink-0">{i < n ? <Check size={15} style={{ color: "#6F5AA8" }} /> : null}</span>{l}
          </motion.p>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ the path */
function Path({ data, core, onOpenDay, onRedo, onEntrance }) {
  const days = data.days || {};
  const doneCount = DAYS.filter((d) => days[d.n]?.done).length;
  const openDay = Math.min(7, doneCount + 1);
  const [showRead, setShowRead] = useState(false);

  return (
    <div className="w-full" data-testid="mental-path">
      <button onClick={onEntrance} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold mb-6 transition-transform hover:-translate-x-0.5"
        style={{ background: "rgba(255,255,255,0.8)", border: "1px solid rgba(46,38,64,0.08)", color: INK }} data-testid="path-to-entrance">
        <ArrowLeft size={13} /> Back to the entrance
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        {/* the read + week one */}
        <div className="lg:col-span-7">
          <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(46,38,64,0.58)" }}>Made for you, {data.name || "you"}</p>
          <h2 className="font-display text-[32px] sm:text-[42px] leading-[1.04] mt-3" style={{ color: INK }}>{core.say}</h2>
          <button onClick={() => setShowRead(!showRead)} className="mt-3 text-[13px] inline-flex items-center gap-1.5 font-semibold" style={{ color: core.hue }} data-testid="path-read-toggle">
            {showRead ? "less" : "why this is your starting point"} <ChevronDown size={12} style={{ transform: showRead ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
          </button>
          <AnimatePresence initial={false}>
            {showRead && (
              <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden text-[15px] leading-[1.8] mt-3 max-w-lg" style={{ color: SOFT }}>
                {core.read}
              </motion.p>
            )}
          </AnimatePresence>

          <div className="mt-8 rounded-[1.8rem] p-6 sm:p-7" style={{ background: "rgba(255,255,255,0.85)", border: "1px solid rgba(46,38,64,0.08)", boxShadow: "0 30px 60px -40px rgba(46,38,64,0.5)", backdropFilter: "blur(8px)" }}>
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: core.hue }}>Open now</p>
                <h3 className="font-display text-2xl mt-1" style={{ color: INK }}>{WEEKS[0].title}</h3>
              </div>
              <p className="text-[11px]" style={{ color: SOFT }}>{doneCount} of 7 closed</p>
            </div>
            <p className="text-sm mt-2" style={{ color: SOFT }}>{WEEKS[0].line}</p>

            <div className="mt-5 space-y-1.5">
              {DAYS.map((d) => {
                const st = days[d.n] || {};
                const locked = d.n > openDay;
                return (
                  <button key={d.n} onClick={() => !locked && onOpenDay(d.n - 1)} disabled={locked}
                    className={`w-full flex items-center gap-3.5 rounded-2xl px-4 py-3 text-left transition-all ${locked ? "opacity-45 cursor-default" : "hover:translate-x-1"}`}
                    style={{ background: d.n === openDay && !st.done ? `${core.hue}14` : "rgba(46,38,64,0.03)" }}
                    data-testid={`day-row-${d.n}`}>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
                      style={st.done ? { background: core.hue, color: "#fff" } : locked ? { background: "rgba(46,38,64,0.07)", color: "rgba(46,38,64,0.58)" } : { background: "#fff", border: `1.5px solid ${core.hue}`, color: core.hue }}>
                      {st.done ? <Check size={12} /> : locked ? <Lock size={10} /> : d.n}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[10px] tracking-[0.16em] uppercase" style={{ color: "rgba(46,38,64,0.58)" }}>Day {d.n}</span>
                      <span className="block font-display text-[17px] leading-tight truncate" style={{ color: INK }}>{locked ? "Opens when the day before closes" : d.title}</span>
                    </span>
                    {!locked && !st.done && <span className="ml-auto shrink-0 rounded-full px-3 py-1 text-[10px] font-semibold text-white" style={{ background: core.hue }}>Begin</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* locked weeks */}
          <div className="mt-4 space-y-1.5">
            {WEEKS.slice(1).map((w, i) => (
              <motion.div key={w.n} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3.5 rounded-2xl px-4 py-3" style={{ background: "rgba(255,255,255,0.5)", border: "1px dashed rgba(46,38,64,0.12)", opacity: 1 - i * 0.07 }}
                data-testid={`week-locked-${w.n}`}>
                <Lock size={12} style={{ color: "rgba(46,38,64,0.3)" }} className="shrink-0" />
                <div className="min-w-0">
                  <p className="font-display text-[16px] leading-tight" style={{ color: "rgba(46,38,64,0.75)" }}>{w.title}</p>
                  <p className="text-[12px] truncate" style={{ color: "rgba(46,38,64,0.58)" }}>{w.line}</p>
                </div>
              </motion.div>
            ))}
            <button onClick={onRedo} className="text-[11px] inline-flex items-center gap-1.5 mt-2" style={{ color: "rgba(46,38,64,0.58)" }} data-testid="mental-redo">
              <RotateCcw size={10} /> answer differently
            </button>
          </div>

          <div className="mt-6 rounded-2xl px-5 py-4 flex items-start gap-4 max-w-md" style={{ background: "rgba(111,90,168,0.12)" }}>
            <p className="font-display text-3xl leading-none shrink-0" style={{ color: "#6F5AA8" }}>15</p>
            <p className="text-[13px] pt-1" style={{ color: INK }}>minutes of screen. The rest happens away from it, which is the point.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================= the room right now */
const MOODS = [
  { key: "heavy", label: "Heavy", tone: "#8A93C2" },
  { key: "between", label: "In between", tone: "#B8A0D8" },
  { key: "ready", label: "Ready", tone: "#6F5AA8" },
];

function RoomNow() {
  const dots = useMemo(
    () => Array.from({ length: 96 }, (_, i) => {
      const r = Math.sin(i * 12.9898) * 43758.5453;
      const f = r - Math.floor(r);
      return { m: f < 0.34 ? 0 : f < 0.68 ? 1 : 2, d: (i % 13) * 0.22 + f * 1.4 };
    }),
    []
  );
  const [hover, setHover] = useState(null);
  const counts = useMemo(() => MOODS.map((_, n) => dots.filter((d) => d.m === n).length), [dots]);

  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(165deg, #3A2F5C, #2A2148 72%)" }} data-testid="mental-room-now">
      <span className="absolute left-[12%] -top-28 w-[30vw] h-[30vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(167,139,250,0.4), transparent 68%)", filter: "blur(44px)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(255,255,255,0.1)", color: "#DCCFFF" }}>
            <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: "#C4B5FD" }} />live, and nameless
          </span>
          <h2 className="font-display text-[34px] sm:text-[46px] leading-[1.02] mt-4 text-white">You are not the only one awake with this</h2>
          <p className="text-[15px] mt-4 max-w-sm text-white/55">Every dot is someone who arrived today and said how they were. Nobody knows which one is which.</p>
          <div className="mt-7 space-y-2">
            {MOODS.map((m, n) => (
              <div key={m.key} className="flex items-center gap-3" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(null)} data-testid={`room-legend-${m.key}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: m.tone }} />
                <span className="text-[13.5px] text-white/70 w-28">{m.label}</span>
                <span className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
                  <motion.span className="block h-full rounded-full" style={{ background: m.tone }} initial={{ width: 0 }} whileInView={{ width: `${(counts[n] / 96) * 100}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.15 * n }} />
                </span>
                <span className="text-[12px] text-white/40 w-8 text-right tabular-nums">{counts[n]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="flex flex-wrap gap-2 sm:gap-2.5">
            {dots.map((d, i) => (
              <motion.span key={i} className="rounded-full"
                style={{ width: 15, height: 15, background: MOODS[d.m].tone, opacity: hover === null || hover === d.m ? 1 : 0.16 }}
                initial={{ scale: 0, opacity: 0 }} whileInView={{ scale: 1, opacity: hover === null || hover === d.m ? 1 : 0.16 }} viewport={{ once: true }}
                transition={{ delay: i * 0.006, type: "spring", damping: 13 }}>
                <motion.span className="block w-full h-full rounded-full" animate={{ scale: [1, 1.28, 1], opacity: [1, 0.5, 1] }}
                  transition={{ duration: 3.2 + (i % 5) * 0.6, repeat: Infinity, delay: d.d, ease: "easeInOut" }} style={{ background: "inherit" }} />
              </motion.span>
            ))}
          </div>
          <p className="text-[12px] mt-6 text-white/35">96 arrivals in the last twelve hours. No names, no locations, nothing you could trace back.</p>
        </div>
      </div>
    </section>
  );
}

/* ======================================== eight weeks, dragged */
const NOISE = Array.from({ length: 22 }, (_, i) => {
  const r = Math.sin((i + 3) * 78.233) * 43758.5453;
  return (r - Math.floor(r)) * 2 - 1;
});

function WeeksDrag({ hue }) {
  const [w, setW] = useState(1);
  const t = (w - 1) / 7;
  const d = useMemo(() => {
    const pts = NOISE.map((n, i) => {
      const x = 20 + (i * 1000) / (NOISE.length - 1);
      const y = 150 - t * 78 + n * (1 - t) * 46 + n * 5 + Math.sin(i * 0.8) * (1 - t) * 10;
      return `${x.toFixed(1)} ${y.toFixed(1)}`;
    });
    return `M ${pts.join(" L ")}`;
  }, [t]);

  return (
    <section className="relative" style={{ background: "linear-gradient(180deg, #F7F3FB, #F0E9FA)" }} data-testid="mental-weeks-drag">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-5 mb-9">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(46,38,64,0.58)" }}>Drag it and watch</p>
            <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2 max-w-md" style={{ color: INK }}>What eight weeks does to the line</h2>
          </div>
          <div className="text-right">
            <p className="font-display text-[52px] leading-none" style={{ color: hue }}>{String(w).padStart(2, "0")}</p>
            <p className="text-[11px] tracking-[0.18em] uppercase mt-1" style={{ color: "rgba(46,38,64,0.58)" }}>of eight</p>
          </div>
        </div>

        <div className="rounded-[2rem] p-6 sm:p-8 relative overflow-hidden" style={{ background: "#fff", border: "1px solid rgba(46,38,64,0.07)", boxShadow: "0 40px 80px -60px rgba(46,38,64,0.6)" }}>
          <svg viewBox="0 0 1040 200" className="w-full h-[170px] sm:h-[210px]" preserveAspectRatio="none">
            <defs>
              <linearGradient id="wline" gradientUnits="userSpaceOnUse" x1="20" y1="0" x2="1020" y2="0">
                <stop offset="0%" stopColor="#B8A0D8" /><stop offset="100%" stopColor={hue} />
              </linearGradient>
            </defs>
            {[40, 90, 140, 190].map((y) => <line key={y} x1="20" x2="1020" y1={y} y2={y} stroke="rgba(46,38,64,0.06)" strokeWidth="1" />)}
            <motion.path d={d} animate={{ d }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              fill="none" stroke="url(#wline)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <input type="range" min={1} max={8} value={w} onChange={(e) => setW(Number(e.target.value))}
            className="w-full mt-5 accent-current" style={{ accentColor: hue }} data-testid="weeks-drag-slider" aria-label="week" />
          <div className="flex justify-between mt-2">
            {WEEKS.map((x) => (
              <button key={x.n} onClick={() => setW(x.n)} className="text-[11px] font-semibold tabular-nums transition-colors"
                style={{ color: x.n === w ? hue : "rgba(46,38,64,0.28)" }} data-testid={`weeks-drag-${x.n}`}>{x.n}</button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={w} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
              className="mt-7 flex items-center gap-3 rounded-2xl px-5 py-4" style={{ background: `${hue}0F` }}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: hue }} />
              <p className="text-[14px]" style={{ color: SOFT }}>{w < 4 ? "Early on it still drops without warning. You are only learning to see it." : w < 7 ? "The drops are still there, but you can feel them coming now." : "Same weather, read earlier. That is the whole difference."}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <p className="text-[12px] mt-5 max-w-md" style={{ color: "rgba(46,38,64,0.58)" }}>The line never becomes flat, and it is not supposed to. It becomes something you can read before it drops.</p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- the offerings */
function SpaceBand({ hue }) {
  const o = OFFERINGS[0];
  const [tab, setTab] = useState("read");
  const active = o.tabs.find((t) => t.k === tab);
  const ICONS = { read: BookOpen, draw: Palette, listen: Moon, write: PenLine };
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #F5F0FC, #EDE5FA)" }} data-testid="offering-space">
      <span className="absolute -left-24 -top-24 w-[36vw] h-[36vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(139,113,206,0.34), transparent 68%)", filter: "blur(30px)" }} />
      <span className="absolute right-[6%] bottom-[4%] w-[22vw] h-[22vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(232,184,75,0.2), transparent 70%)", filter: "blur(34px)", animationDelay: "3s" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(111,90,168,0.16)", color: "#5C4A8E" }}>
              <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: "#6F5AA8" }} />{o.unlock}
            </span>
            <h2 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-4" style={{ color: INK }}>{o.name}</h2>
            <p className="text-[15px] mt-3" style={{ color: SOFT }}>{o.line}</p>
            <div className="mt-7 space-y-1.5">
              {o.tabs.map((t) => {
                const Ic = ICONS[t.k];
                const on = tab === t.k;
                return (
                  <button key={t.k} onClick={() => setTab(t.k)} className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all hover:translate-x-1"
                    style={on ? { background: "#fff", boxShadow: "0 16px 34px -22px rgba(58,42,102,0.6)" } : { background: "rgba(255,255,255,0.5)" }}
                    data-testid={`space-tab-${t.k}`}>
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: on ? "rgba(111,90,168,0.2)" : "rgba(46,38,64,0.05)", color: on ? "#5C4A8E" : "rgba(46,38,64,0.5)" }}><Ic size={15} /></span>
                    <span className="font-display text-[17px]" style={{ color: INK }}>{t.t}</span>
                    <ArrowRight size={13} className="ml-auto" style={{ color: on ? "#5C4A8E" : "transparent" }} />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div key={tab} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-[2rem] overflow-hidden h-full" style={{ background: "#fff", border: "1px solid rgba(58,42,102,0.08)", boxShadow: "0 40px 70px -50px rgba(58,42,102,0.7)" }}>
                <div className="h-[190px] relative flex items-end p-7 overflow-hidden" style={{ background: tab === "draw" ? "repeating-linear-gradient(45deg, #F1EAFB, #F1EAFB 14px, #E6DBF7 14px, #E6DBF7 28px)" : tab === "listen" ? `linear-gradient(150deg, ${hue}, #2A2148)` : tab === "write" ? "linear-gradient(160deg, #FFFDF8, #F7F1E7)" : `linear-gradient(150deg, ${hue}, #8B71CE)` }}>
                  {tab === "listen" && (
                    <>
                      {[0, 1, 2].map((k) => (
                        <motion.span key={k} className="absolute rounded-full" style={{ left: "50%", top: "50%", width: 120 + k * 78, height: 120 + k * 78, marginLeft: -(60 + k * 39), marginTop: -(60 + k * 39), border: "1px solid rgba(255,255,255,0.22)" }}
                          animate={{ scale: [0.9, 1.12, 0.9], opacity: [0.25, 0.6, 0.25] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: k * 1.6 }} />
                      ))}
                      <motion.span className="absolute rounded-full" style={{ left: "50%", top: "50%", width: 74, height: 74, marginLeft: -37, marginTop: -37, background: "radial-gradient(circle at 38% 32%, rgba(255,255,255,0.95), rgba(255,232,190,0.5) 55%, rgba(255,232,190,0) 78%)" }}
                        animate={{ scale: [0.94, 1.06, 0.94], opacity: [0.8, 1, 0.8] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} />
                      <svg viewBox="0 0 600 120" className="absolute left-0 right-0 bottom-0 w-full h-[70px]" preserveAspectRatio="none">
                        {[0, 1].map((k) => (
                          <motion.path key={k} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth={k ? 1 : 1.8} strokeLinecap="round"
                            initial={{ d: "M 0 70 Q 150 40 300 70 T 600 70" }}
                            animate={{ d: ["M 0 70 Q 150 40 300 70 T 600 70", "M 0 70 Q 150 96 300 70 T 600 70", "M 0 70 Q 150 40 300 70 T 600 70"] }}
                            transition={{ duration: 7 + k * 2.5, repeat: Infinity, ease: "easeInOut", delay: k * 0.8 }} />
                        ))}
                      </svg>
                      <p className="relative text-[11px] tracking-[0.2em] uppercase text-white/60">nine minutes · nothing to watch</p>
                    </>
                  )}
                  {tab === "read" && <p className="font-display text-[26px] leading-tight max-w-sm text-white">{active.teaser}</p>}
                  {tab === "draw" && (
                    <svg viewBox="0 0 400 120" className="absolute inset-0 w-full h-full">
                      <path className="draw-in" d="M 30 90 C 90 20, 150 110, 210 50 C 260 5, 320 80, 375 40" fill="none" stroke="#6F5AA8" strokeWidth="3" strokeLinecap="round" pathLength={1} />
                    </svg>
                  )}
                  {tab === "write" && (
                    <div className="relative w-full">
                      <span className="absolute -right-4 -top-16 w-32 h-32 rounded-full" style={{ background: "radial-gradient(circle, rgba(232,184,75,0.28), transparent 70%)" }} />
                      <p className="text-[10px] tracking-[0.2em] uppercase mb-3" style={{ color: "rgba(46,38,64,0.35)" }}>tonight, three lines, nobody reads them</p>
                      <div className="flex items-baseline">
                        <motion.p className="font-display text-[24px] sm:text-[27px] whitespace-nowrap overflow-hidden" style={{ color: "rgba(46,38,64,0.82)" }}
                          initial={{ width: 0 }} animate={{ width: ["0%", "100%", "100%", "0%"] }} transition={{ duration: 9, times: [0, 0.45, 0.85, 1], repeat: Infinity, ease: "easeInOut" }}>
                          Today I noticed that
                        </motion.p>
                        <motion.span className="inline-block w-[2px] h-[26px] ml-1 shrink-0" style={{ background: "#6F5AA8" }}
                          animate={{ opacity: [1, 0, 1] }} transition={{ duration: 1.1, repeat: Infinity }} />
                      </div>
                      <span className="block mt-4 h-px w-full" style={{ background: "rgba(46,38,64,0.14)" }} />
                      <span className="block mt-4 h-px w-2/3" style={{ background: "rgba(46,38,64,0.08)" }} />
                    </div>
                  )}
                </div>
                <div className="p-7">
                  <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "rgba(46,38,64,0.58)" }}>{active.t}</p>
                  <p className="font-display text-[22px] leading-snug mt-2" style={{ color: INK }}>{active.d}</p>
                  <p className="text-[13.5px] mt-3 italic" style={{ color: SOFT }}>{active.teaser}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function CirclesBand() {
  const o = OFFERINGS[1];
  const [on, setOn] = useState(0);
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(165deg, #3A2F5C, #241C40 70%)" }} data-testid="offering-circles">
      <span className="absolute right-[8%] -top-24 w-[34vw] h-[34vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(167,139,250,0.45), transparent 68%)", filter: "blur(40px)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(255,255,255,0.12)", color: "#DCCFFF" }}>
              <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: "#C4B5FD" }} />{o.unlock}
            </span>
            <h2 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-4 text-white">{o.name}</h2>
            <p className="text-[15px] mt-3 max-w-md text-white/55">{o.line}</p>
          </div>
          <div className="flex items-center gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <motion.span key={i} className="w-8 h-8 rounded-full -ml-3 first:ml-0" style={{ background: `hsl(${255 + i * 12} 40% ${58 + i * 4}%)`, border: "2px solid #2C2350" }}
                animate={{ y: [0, -3, 0] }} transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut" }} />
            ))}
            <span className="text-[12px] text-white/45 ml-2">825 aliases inside</span>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-5 space-y-2">
            {o.rooms.map((r, i) => (
              <motion.button key={r.t} initial={{ opacity: 0, x: -14 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                onClick={() => setOn(i)} className="w-full text-left rounded-2xl px-5 py-4 transition-all hover:translate-x-1"
                style={on === i ? { background: "rgba(255,255,255,0.13)", border: "1px solid rgba(196,181,253,0.4)" } : { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`circle-${i}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-display text-[17px] leading-tight text-white">{r.t}</p>
                  <span className="text-[11px] shrink-0" style={{ color: "#C4B5FD" }}>{r.n}</span>
                </div>
              </motion.button>
            ))}
          </div>
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div key={on} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}
                className="rounded-[2rem] p-7 h-full" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(14px)" }}>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">inside this room</p>
                <p className="font-display text-2xl text-white mt-2">{o.rooms[on].t}</p>
                <div className="mt-5 space-y-2.5">
                  {["Anyone can post without a name, a photograph or a follower count.", o.rooms[on].pin, "Nothing is ranked, nothing is public, and nothing leaves the room."].map((line, i) => (
                    <motion.div key={line} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.1 }}
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-[13.5px] ${i === 1 ? "ml-auto" : ""}`}
                      style={i === 1 ? { background: "rgba(196,181,253,0.22)", color: "#fff" } : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.8)" }}>
                      {line}
                    </motion.div>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-2 text-[12px] text-white/40">
                  <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: "#6FA88A" }} />a specialist is in this room most evenings
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function ExpectBand({ hue }) {
  const [on, setOn] = useState(0);
  return (
    <section className="relative" style={{ background: "#F7F3FB" }} data-testid="expect-band">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(46,38,64,0.58)" }}>Before you start</p>
        <h2 className="font-display text-[32px] sm:text-[40px] leading-[1.04] mt-2 mb-9" style={{ color: INK }}>The things people ask first</h2>
        <div className="space-y-2">
          {EXPECT.map((e, i) => (
            <div key={e.q} className="rounded-2xl overflow-hidden" style={{ background: on === i ? "#fff" : "rgba(255,255,255,0.6)", border: "1px solid rgba(46,38,64,0.07)" }}>
              <button onClick={() => setOn(on === i ? -1 : i)} className="w-full flex items-center gap-4 px-6 py-4 text-left" data-testid={`expect-${i}`}>
                <span className="font-display text-lg" style={{ color: on === i ? hue : "rgba(46,38,64,0.3)" }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-[18px] flex-1" style={{ color: INK }}>{e.q}</span>
                <ChevronDown size={15} style={{ color: "rgba(46,38,64,0.35)", transform: on === i ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
              </button>
              <AnimatePresence initial={false}>
                {on === i && (
                  <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden px-6 pb-5 pl-[70px] text-[15px] leading-[1.8]" style={{ color: SOFT }}>{e.a}</motion.p>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OneBand({ hue }) {
  const o = OFFERINGS.find((x) => x.key === "one");
  const [waiting, setWaiting] = useState(false);
  return (
    <section className="relative" style={{ background: "#FFFFFF" }} data-testid="offering-one">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: `${hue}18`, color: hue }}>
              <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: hue }} />{o.unlock}
            </span>
            <h2 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-4" style={{ color: INK }}>{o.name}</h2>
            <p className="text-[15px] mt-3" style={{ color: SOFT }}>{o.line}</p>
            <button onClick={() => setWaiting(true)} className="mt-7 inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
              style={{ background: hue, boxShadow: `0 20px 44px -20px ${hue}` }} data-testid="one-to-one-waitlist">
              {waiting ? "Your place is kept. Nothing else needed." : "Keep a place for me"}{!waiting && <ArrowRight size={15} />}
            </button>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {o.steps.map((s, i) => (
              <motion.div key={s} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-[1.6rem] p-6" style={{ background: i === 1 ? `${hue}0F` : "rgba(46,38,64,0.035)", marginTop: i * 14 }}>
                <p className="font-display text-4xl leading-none" style={{ color: `${hue}59` }}>{i + 1}</p>
                <p className="text-[14px] mt-3" style={{ color: INK }}>{s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================== page */
export default function MentalHealth() {
  const [data, patch] = useProgress("mental", { name: "", coreKey: null, answers: {}, days: {} });
  const [phase, setPhase] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const core = data.coreKey ? CORES[data.coreKey] : null;
  const stage = phase || "welcome";
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [stage]);
  const doneCount = useMemo(() => DAYS.filter((d) => (data.days || {})[d.n]?.done).length, [data.days]);

  const finish = (answers, name) => {
    const { core: found } = readOf(answers);
    patch({ answers, name, coreKey: found.key, days: {} });
    setPhase("reading");
  };
  const saveDay = (n, p) => patch((d) => ({ days: { ...(d.days || {}), [n]: { ...((d.days || {})[n] || {}), ...p } } }));

  return (
    <main className="pt-16" style={{ background: PAPER }} data-testid="platform-page-mental">
      {/* ============================== the stage */}
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: "calc(100svh - 4rem)" }} data-testid="mental-stage">
        <motion.div className="absolute -inset-y-[8%] inset-x-0" initial={{ scale: 1.12 }} animate={{ scale: 1.02 }} transition={{ duration: 26, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          style={{ y: bgY, backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center", filter: "saturate(1.05) contrast(1.02)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(238,231,250,0.42) 0%, rgba(226,215,247,0.7) 40%, rgba(243,238,251,0.95) 86%, #F7F3FB 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(46% 42% at 78% 12%, rgba(139,113,206,0.34), transparent 72%), radial-gradient(44% 44% at 4% 86%, rgba(111,90,168,0.3), transparent 72%), radial-gradient(26% 22% at 62% 78%, rgba(232,184,75,0.14), transparent 74%)" }} />

        <motion.div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 flex flex-col justify-center" style={{ minHeight: "calc(100svh - 4rem)", y: contentY }}>
          <AnimatePresence mode="wait">
            {stage === "welcome" && (
              <motion.div key="welcome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -20, filter: "blur(8px)" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center w-full">
                <div className="lg:col-span-6">
                  <h1 className="font-display text-[40px] sm:text-[58px] lg:text-[68px] leading-[0.98] mt-5" style={{ color: INK }}>
                    {[["Come", 0], ["in.", 1], ["\n", -1], ["You", 2], ["can", 3], ["put", 4], ["\n", -1], ["it", 5], ["down", 6], ["here.", 7]].map(([w, i], n) =>
                      w === "\n" ? <br key={n} /> : (
                        <motion.span key={n} initial={{ opacity: 0, y: 22, filter: "blur(7px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                          transition={{ delay: 0.18 + i * 0.085, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                          className="inline-block mr-[0.24em]" style={{ color: i >= 5 ? "#6F5AA8" : INK }}>{w}</motion.span>
                      ))}
                  </h1>
                  <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }} className="text-[16px] sm:text-[18px] mt-6 max-w-md leading-relaxed" style={{ color: SOFT }}>
                    Tell me a little. Your first days get built around exactly that, one at a time, in private.
                  </motion.p>
                  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-8 flex flex-wrap items-center gap-4">
                    <button onClick={() => setPhase(core ? "path" : "talk")} className="group inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform hover:-translate-y-0.5"
                      style={{ background: "#6F5AA8", boxShadow: "0 24px 50px -20px rgba(111,90,168,0.9)" }} data-testid="mental-begin">
                      {core ? `Continue, day ${Math.min(7, doneCount + 1)}` : "Start where you are"}
                      <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="text-[13px]" style={{ color: "rgba(46,38,64,0.45)" }}>Four minutes. No account,<br className="hidden sm:block" /> nothing kept anywhere but here.</p>
                  </motion.div>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="mt-9 flex flex-wrap gap-x-6 gap-y-2">
                    {[["8,412", "days closed this month"], ["11 min", "an average day"], ["0", "names collected"]].map(([a, b]) => (
                      <div key={b}>
                        <p className="font-display text-2xl leading-none" style={{ color: "#6F5AA8" }}>{a}</p>
                        <p className="text-[11px] mt-1" style={{ color: "rgba(46,38,64,0.45)" }}>{b}</p>
                      </div>
                    ))}
                  </motion.div>
                </div>

                {/* the visual: put it down here */}
                <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="lg:col-span-6 relative" data-testid="mental-hero-animation">
                  <MentalHero onBegin={() => setPhase(core ? "path" : "talk")} />
                </motion.div>
              </motion.div>
            )}
            {stage === "talk" && <Talk key="talk" onDone={finish} onLeave={() => setPhase("welcome")} />}
            {stage === "reading" && <Reading key="reading" onDone={() => setPhase("path")} />}
            {stage === "path" && core && (
              <motion.div key="path" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="w-full">
                <Path data={data} core={core} onOpenDay={setOpenDay} onEntrance={() => setPhase("welcome")} onRedo={() => { patch({ coreKey: null, days: {} }); setPhase("talk"); }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {stage !== "path" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute left-0 right-0 bottom-5 flex justify-center pointer-events-none">
            <p className="text-[10px] tracking-[0.2em] uppercase flex items-center gap-2" style={{ color: "rgba(46,38,64,0.3)" }}>
              there is more below, but this comes first <ChevronDown size={12} className="floaty" />
            </p>
          </motion.div>
        )}
      </section>

      {/* ============================== the entrance only, never over the path */}
      {stage !== "path" && (
        <>
          <RoomNow />
          <WeeksDrag hue={core?.hue || "#6F5AA8"} />
        </>
      )}

      {/* ============================== offerings, entrance only */}
      {stage !== "path" ? (
        <>
          <section className="relative" style={{ background: "linear-gradient(180deg, #F7F3FB, #EFE9F9)" }}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 text-center">
              <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(46,38,64,0.58)" }}>Beyond your days</p>
              <h2 className="font-display text-[30px] sm:text-[40px] mt-2 leading-[1.04] max-w-xl mx-auto" style={{ color: INK }}>Three rooms opening around you</h2>
            </div>
          </section>
          <SpaceBand hue={core?.hue || "#6F5AA8"} />
          <CirclesBand />
          <OneBand hue={core?.hue || "#6F5AA8"} />
          <ExpectBand hue={core?.hue || "#6F5AA8"} />
        </>
      ) : (
        <InProgramme hue={core?.hue || "#6F5AA8"} doneCount={doneCount} onOpenDay={() => setOpenDay(Math.min(6, doneCount))} />
      )}

      <section style={{ background: "linear-gradient(180deg, #EFE9F9, #F7F3FB)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
          <div className="rounded-[2rem] px-7 py-9 flex flex-col sm:flex-row sm:items-center gap-5 relative overflow-hidden" style={{ background: `linear-gradient(140deg, ${core?.hue || "#6F5AA8"}, #4B3B7A)` }}>
            <span className="absolute -right-16 -top-16 w-52 h-52 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(232,184,75,0.4), transparent 70%)" }} />
            <p className="font-display text-xl sm:text-[26px] leading-snug flex-1 text-white relative">
              {core ? `Day ${Math.min(7, doneCount + 1)} is waiting, ${data.name || "you"}. Fifteen minutes.` : "The first days get built the moment you tell me a little."}
            </p>
            <button onClick={() => { setPhase(core ? "path" : "talk"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="shrink-0 relative rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: "#fff", color: INK }} data-testid="mental-bottom-cta">
              {core ? "Back to my path" : "Start where you are"}
            </button>
          </div>
        </div>
      </section>

      <section style={{ background: "#F1ECF8", borderTop: "1px solid rgba(46,38,64,0.07)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <p className="kicker mb-4" style={{ color: "rgba(46,38,64,0.58)" }}>The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== "mental").map((x) => (
              <Link key={x.key} to={x.path} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5"
                style={{ background: "#fff", border: "1px solid rgba(46,38,64,0.08)", color: INK }} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />{x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {openDay !== null && core && (
          <DayFlow
            dayIndex={openDay} core={core} name={data.name}
            state={(data.days || {})[openDay + 1]}
            onSave={(p) => saveDay(openDay + 1, p)}
            onClose={() => setOpenDay(null)}
            onComplete={() => { saveDay(openDay + 1, { done: true }); setOpenDay(null); }}
          />
        )}
      </AnimatePresence>
    </main>
  );
}
